import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { t } from '@/i18n';
import type { KsefLineItem, ProjectOption } from '@ip/shared-types';

const NONE_VALUE = '__none__';

interface InvoiceLineItemsTableProps {
  lineItems: KsefLineItem[];
  currency: string | null;
  /**
   * Task B3: when provided (together with onLineProjectChange), each row gets
   * a project Select — an assigned line counts to its own project; lines left
   * unassigned inherit the invoice's project (the residual rule).
   */
  projects?: ProjectOption[];
  onLineProjectChange?: (lineItemId: string, projectId: string | null) => void;
  /**
   * The invoice's own (whole-invoice) project, if any — unassigned lines
   * inherit it, so their Select shows "Dziedziczy: <name>" instead of "Brak".
   */
  inheritedProject?: { name: string; color: string | null } | null;
}

const formatNumber = (value: number | null): string => {
  if (value === null) return '-';
  return value.toLocaleString('pl-PL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatQuantity = (value: number | null): string => {
  if (value === null) return '-';
  return value.toLocaleString('pl-PL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  });
};

const formatVatRate = (rate: string | null): string => {
  if (!rate) return '-';
  const numRate = parseFloat(rate);
  if (isNaN(numRate)) return rate;
  return `${rate}%`;
};

/** Active projects plus — so the current value always renders — the line's own (possibly closed) project. */
function selectableProjects(projects: ProjectOption[], currentId: string | null | undefined) {
  const active = projects.filter((p) => p.status === 'active');
  if (currentId && !active.some((p) => p.id === currentId)) {
    const current = projects.find((p) => p.id === currentId);
    if (current) return [...active, current];
  }
  return active;
}

export function InvoiceLineItemsTable({
  lineItems,
  currency,
  projects,
  onLineProjectChange,
  inheritedProject,
}: InvoiceLineItemsTableProps) {
  const totalNet = lineItems.reduce((sum, li) => sum + (li.netValue ?? 0), 0);
  const totalVat = lineItems.reduce((sum, li) => sum + (li.vatValue ?? 0), 0);
  const showProjects = Boolean(projects && projects.length > 0 && onLineProjectChange);

  return (
    <div className="flex min-h-0 flex-col">
      <div className="border-b px-4 py-3">
        <p className="text-sm font-semibold">Pozycje faktury</p>
        <p className="text-muted-foreground text-xs">
          Faktura wystawiona w cenach netto w walucie {currency || 'PLN'}
        </p>
      </div>
      <div className="min-h-0 flex-1 overflow-visible [&_[data-slot=table-container]]:overflow-visible">
        <Table>
          <TableHeader className="bg-card sticky top-0 z-10">
            <TableRow>
              <TableHead className="w-12 text-center">Lp.</TableHead>
              <TableHead className="w-20 text-center">Nr wiersza</TableHead>
              <TableHead>Nazwa towaru lub usługi</TableHead>
              <TableHead className="w-24 text-right">Cena jedn.</TableHead>
              <TableHead className="w-16 text-right">Ilość</TableHead>
              <TableHead className="w-16 text-center">Miara</TableHead>
              <TableHead className="w-16 text-center">Stawka</TableHead>
              <TableHead className="w-24 text-right">Netto</TableHead>
              <TableHead className="w-20 text-right">VAT</TableHead>
              {showProjects && (
                <TableHead className="w-40">{t('projects.selector.label')}</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {lineItems.map((li) => (
              <TableRow key={li.ordinal}>
                <TableCell className="text-center">{li.ordinal}</TableCell>
                <TableCell className="text-center">{li.lineNumber || '-'}</TableCell>
                <TableCell className="max-w-0">
                  <span className="block truncate" title={li.description}>
                    {li.description}
                  </span>
                </TableCell>
                <TableCell className="text-right tabular-nums">{formatNumber(li.unitPrice)}</TableCell>
                <TableCell className="text-right tabular-nums">{formatQuantity(li.quantity)}</TableCell>
                <TableCell className="text-center">{li.unit || '-'}</TableCell>
                <TableCell className="text-center">{formatVatRate(li.vatRate)}</TableCell>
                <TableCell className="text-right tabular-nums">{formatNumber(li.netValue)}</TableCell>
                <TableCell className="text-right tabular-nums">{formatNumber(li.vatValue)}</TableCell>
                {showProjects && (
                  <TableCell>
                    <Select
                      value={li.projectId ?? NONE_VALUE}
                      onValueChange={(v) =>
                        onLineProjectChange?.(li.id, v === NONE_VALUE ? null : v)
                      }
                    >
                      <SelectTrigger size="sm" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE_VALUE}>
                          {inheritedProject ? (
                            <span className="text-muted-foreground flex items-center gap-2">
                              <span
                                className="inline-block size-2 shrink-0 rounded-full border"
                                style={{ backgroundColor: inheritedProject.color ?? 'transparent' }}
                                aria-hidden="true"
                              />
                              <span className="truncate italic">
                                {t('projects.selector.inherits')} {inheritedProject.name}
                              </span>
                            </span>
                          ) : (
                            <span className="text-muted-foreground">{t('projects.selector.none')}</span>
                          )}
                        </SelectItem>
                        {selectableProjects(projects!, li.projectId).map((project) => (
                          <SelectItem key={project.id} value={project.id}>
                            <span className="flex items-center gap-2">
                              <span
                                className="inline-block size-2 shrink-0 rounded-full border"
                                style={{ backgroundColor: project.color ?? 'transparent' }}
                                aria-hidden="true"
                              />
                              <span className="truncate">
                                {project.name}
                                {project.status === 'closed'
                                  ? ` ${t('projects.selector.closedSuffix')}`
                                  : ''}
                              </span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
          <TableFooter className="bg-card sticky bottom-0 z-10">
            <TableRow>
              <TableCell colSpan={7} className="text-right font-semibold">
                Razem:
              </TableCell>
              <TableCell className="text-right font-semibold tabular-nums">
                {formatNumber(totalNet)}
              </TableCell>
              <TableCell className="text-right font-semibold tabular-nums">
                {formatNumber(totalVat)}
              </TableCell>
              {showProjects && <TableCell />}
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </div>
  );
}
